import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaBullseye, FaCalendarDay, FaChurch, FaEnvelope, FaMapMarkerAlt, FaPhone, FaSearch, FaUsers } from 'react-icons/fa';
import { useCampuses } from '../../contexts/CampusContext';
import { useCellMinistries } from '../../contexts/CellMinistryContext';
import { useLeaders } from '../../contexts/LeaderContext';
import PageHeader from '../common/PageHeader';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const cardHover = {
  hover: {
    y: -5,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    transition: {
      duration: 0.3
    }
  }
};

const CellMinistry = () => {
  return (
    <motion.main 
      className="px-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageHeader 
        title="Cell Ministry" 
        subtitle="See what God can do through your service"
        imageUrl="https://i.pinimg.com/1200x/fa/88/76/fa8876108294fe5bfaa73ded4007e453.jpg"
        imageAlt="Church Events"
      />

      <motion.section 
        className="container max-w-4xl mx-auto px-4 py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <motion.div 
          className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-primary"
          whileHover="hover"
          variants={cardHover}
        >
          <motion.p className="text-lg leading-relaxed text-gray-700">
            Cell ministry is the pinnacle of your personal spiritual growth and the
            growth of the church, not only your local church but the global
            church, as in the body of Christ. Cell ministry offers you
            opportunities that a normal Sunday church does not provide, it offers
            an opportunity to grow in the word, prayer and fellowship. It is where
            you can find a group of people that you can do life with.
          </motion.p>
        </motion.div>
      </motion.section>

      <CellGroupLocator/>
    </motion.main>
  );
};

const CellGroupLocator = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampusId, setSelectedCampusId] = useState('all');
  const { campuses } = useCampuses();
  const { leaders } = useLeaders();
  const { cellGroups } = useCellMinistries();
  const [enhancedCellGroups, setEnhancedCellGroups] = useState([]);

  useEffect(() => {
    if (cellGroups && leaders && campuses) {
      const enhanced = cellGroups.map(group => ({
        ...group,
        leader: leaders.find(leader => leader.cellgroupId?.includes(group.id)) || null,
        campus: campuses.find(campus => campus.id === group.campusId) || null
      }));
      setEnhancedCellGroups(enhanced);
    }
  }, [cellGroups, leaders, campuses]);

  const filteredGroups = enhancedCellGroups.filter(group => {
    const matchesSearch = 
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.leader?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCampus = 
      selectedCampusId === 'all' || 
      group.campusId === selectedCampusId;
    
    return matchesSearch && matchesCampus;
  });

  return (
    <motion.div 
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          variants={itemVariants}
        >
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Discover Cell Groups
          </h1>
          <p className="mt-3 text-xl text-gray-500">
            Connect with a community near you
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div 
          className="mb-8 bg-white p-6 rounded-xl shadow-md"
          variants={itemVariants}
        >
          <div className="flex flex-col md:flex-row gap-4">
            <motion.div 
              className="relative flex-grow"
              whileHover={{ scale: 1.01 }}
            >
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Search groups or leaders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </motion.div>
            <motion.div 
              className="relative w-full md:w-64"
              whileHover={{ scale: 1.01 }}
            >
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaChurch className="h-5 w-5 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
              >
                <option value="all">All Campuses</option>
                {campuses?.map(campus => (
                  <option key={campus.id} value={campus.id}>{campus.name}</option>
                ))}
              </select>
            </motion.div>
          </div>
        </motion.div>

        {/* Groups List */}
        <motion.div 
          className="space-y-6"
          variants={containerVariants}
        >
          <AnimatePresence>
            {filteredGroups.length > 0 ? (
              filteredGroups.map((group, index) => (
                <motion.div 
                  key={group.id} 
                  className="bg-white overflow-hidden shadow rounded-xl"
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  custom={index}
                  whileHover="hover"
                  variants={cardHover}
                  exit={{ opacity: 0 }}
                  layout
                >
                  <div className="px-6 py-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold text-gray-900">{group.name}</h2>
                        <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <FaMapMarkerAlt className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                            {group.campus?.name || 'No campus assigned'}
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <FaCalendarDay className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                            {group.meetingTime || 'Meeting time not specified'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Leader Profile */}
                    {group.leader && (
                      <motion.div 
                        className="mt-6 border-t border-gray-100 pt-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Group Leader</h3>
                        <div className="flex items-center space-x-4">
                          <motion.div 
                            className="flex-shrink-0 h-16 w-16 rounded-full bg-gray-100 overflow-hidden border-2 border-primary"
                            whileHover={{ scale: 1.05 }}
                          >
                            {group.leader.profile_image ? (
                              <img className="h-full w-full object-cover" src={group.leader.profile_image} alt={group.leader.name} />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-gray-400">
                                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </motion.div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{group.leader.name}</p>
                            <p className="text-sm text-gray-500 truncate">{group.leader.position || 'Cell Group Leader'}</p>
                            <div className="mt-2 flex space-x-4">
                              {group.leader.phone && (
                                <motion.a 
                                  href={`tel:${group.leader.phone}`} 
                                  className="inline-flex items-center text-sm text-primary hover:text-primary-dark"
                                  whileHover={{ x: 3 }}
                                >
                                  <FaPhone className="mr-1.5 h-4 w-4" />
                                  Call
                                </motion.a>
                              )}
                              {group.leader.email && (
                                <motion.a 
                                  href={`mailto:${group.leader.email}`} 
                                  className="inline-flex items-center text-sm text-primary hover:text-primary-dark"
                                  whileHover={{ x: 3 }}
                                >
                                  <FaEnvelope className="mr-1.5 h-4 w-4" />
                                  Email
                                </motion.a>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Group Details */}
                    <motion.div 
                      className="mt-6 border-t border-gray-100 pt-6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Group Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-500 flex items-center">
                            <FaMapMarkerAlt className="mr-2" /> Location
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">{group.location || 'Not specified'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-500 flex items-center">
                            <FaCalendarDay className="mr-2" /> Meeting Day
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">{group.meetingDay || 'Not specified'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-500 flex items-center">
                            <FaBullseye className="mr-2" /> Focus
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">{group.focus || 'General fellowship'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-500 flex items-center">
                            <FaUsers className="mr-2" /> Members
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">{group.memberCount || 'Not specified'}</p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                  <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <motion.button
                      type="button"
                      className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Join This Group
                    </motion.button>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div 
                className="text-center py-12 bg-white rounded-lg shadow-sm"
                variants={itemVariants}
              >
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">No groups found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || selectedCampusId !== 'all' 
                    ? "Try adjusting your search or filter criteria" 
                    : "There are currently no cell groups available"}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CellMinistry;